import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatCAD } from "@/lib/api";
import { logDebug, logError } from "@/lib/logger";

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const seen = useRef(null); // Set of ids already known
  const audioCtxRef = useRef(null);
  const navigate = useNavigate();

  const go = async (n) => {
    try { await api.post(`/admin/notifications/${n.id}/read`); } catch (e) { logError("Mark notification read failed", e); }
    setOpen(false);
    poll();
    navigate(`/admin?tab=orders&order=${n.order_number}`);
  };

  const playChime = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!audioCtxRef.current) audioCtxRef.current = new AC();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      [880, 1108.73, 1318.51].forEach((f, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = f;
        osc.connect(gain);
        gain.connect(ctx.destination);
        const t = ctx.currentTime + i * 0.14;
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.linearRampToValueAtTime(0.16, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.55);
        osc.start(t);
        osc.stop(t + 0.6);
      });
    } catch (_) {}
  };

  const notifyDesktop = (n) => {
    try {
      if ("Notification" in window && Notification.permission === "granted") {
        const nt = new Notification(`New order ${n.order_number} — Bambam African Mart`, {
          body: `${n.customer_name} · ${formatCAD(n.total_cents)} · ${n.item_count} item(s)`,
          tag: n.id,
        });
        nt.onclick = () => { window.focus(); go(n); nt.close(); };
      }
    } catch (_) {}
  };

  const poll = async () => {
    try {
      const { data } = await api.get("/admin/notifications");
      setItems(data.items);
      setUnread(data.unread);
      if (seen.current === null) {
        seen.current = new Set(data.items.map((n) => n.id));
      } else {
        const fresh = data.items.filter((n) => !seen.current.has(n.id));
        if (fresh.length > 0) playChime();
        fresh.forEach((n) => {
          toast.success(`New order ${n.order_number}`, {
            description: `${n.customer_name} · ${formatCAD(n.total_cents)} · ${n.item_count} item(s)`,
            action: { label: "View", onClick: () => go(n) },
          });
          notifyDesktop(n);
          seen.current.add(n.id);
        });
      }
    } catch (e) {
      logDebug("Notification poll failed", e);
    }
  };

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    poll();
    const t = setInterval(poll, 12000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const markAll = async () => {
    try { await api.post("/admin/notifications/read-all"); } catch (e) { logError("Mark all read failed", e); }
    poll();
  };

  return (
    <div className="relative">
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex h-10 w-10 items-center justify-center rounded-full text-forest transition-colors hover:bg-forest/5"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span data-testid="notification-count" className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-clay px-1 text-[11px] font-bold text-white">
            {unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              data-testid="notification-dropdown"
              className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-2xl border border-border bg-card shadow-xl"
            >
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <span className="font-display text-lg font-medium text-forest">Notifications</span>
                {unread > 0 && (
                  <button onClick={markAll} className="text-xs font-medium text-clay hover:underline">Mark all read</button>
                )}
              </div>
              <div className="max-h-96 overflow-y-auto">
                {items.length === 0 ? (
                  <p className="px-4 py-8 text-center text-sm text-muted-foreground">No notifications yet</p>
                ) : (
                  items.map((n) => (
                    <button
                      key={n.id}
                      data-testid={`notification-${n.order_number}`}
                      onClick={() => go(n)}
                      className={`flex w-full items-start gap-3 border-b border-border px-4 py-3 text-left transition-colors last:border-0 hover:bg-forest/5 ${!n.read ? "bg-clay/5" : ""}`}
                    >
                      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-clay/10 text-clay">
                        <ShoppingBag className="h-4 w-4" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-forest">New order {n.order_number}</p>
                        <p className="text-xs text-muted-foreground">{n.customer_name} · {formatCAD(n.total_cents)}</p>
                        <p className="mt-0.5 text-[11px] text-muted-foreground">{timeAgo(n.created_at)}</p>
                      </div>
                      {!n.read && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-clay" />}
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
